import { useEffect } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { type Theme, useTheme } from "@mui/material/styles";

import { neutral } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import Dialog from "components/dialog";
import { TagTypes } from "constants/tags";
import { useLazyGetAssignedTagHistoryQuery } from "services/assigned-tags";
import type {
  AssignedTagHistoryEvent,
  AssignedTagHistoryTag
} from "services/types/assigned-tag-history.type";
import { formatDateToDateAndTime } from "utils/common";

import { tagColor } from "./assigned-tags.utils";

// Opened from tag lists inside raised dialogs (the staff task modal sits at snackbar + 2).
const HISTORY_DIALOG_SX = {
  zIndex: (theme: Theme) => theme.zIndex.snackbar + 3
};

// Palette tokens, never hex literals — the action colour carries meaning and must follow the
// theme (mirrors the shipped staff-task history table).
const ACTION_COLORS: Record<string, string> = {
  Added: "success.main",
  Removed: "error.main",
  Edited: "warning.main",
  Restored: "info.main"
};

function formatEventDate(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return "--";
  }

  return formatDateToDateAndTime(parsed);
}

type TagHistoryChipProps = {
  tag: AssignedTagHistoryTag;
  removed: boolean;
};

/**
 * A read-only echo of `TagChip`'s look. Deliberately not `TagChip` itself: that component is
 * interactive (click opens the edit popover, which mutates), and every chip here refers to a
 * point in the past — several of them to tags that no longer exist.
 */
function TagHistoryChip({ tag, removed }: TagHistoryChipProps) {
  const { palette } = useTheme();

  return (
    <Chip
      size="small"
      label={tag.name}
      variant="outlined"
      sx={{
        background: tagColor(tag.color),
        color: palette.getContrastText(tagColor(tag.color)),
        border: `solid 1px ${neutral[200]}`,
        fontWeight: 700,
        height: "22px",
        // A removed tag is struck through, matching how an expired tag already reads.
        textDecoration: removed ? "line-through" : "none",
        opacity: removed ? 0.7 : 1
      }}
    />
  );
}

export type TagHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  tagType: TagTypes;
  modelId: number;
  /** Names the record in the dialog subtitle, e.g. "Inbound fax from (303) 752-8090". */
  subtitle?: string;
};

export default function TagHistoryDialog({
  open,
  onClose,
  tagType,
  modelId,
  subtitle
}: TagHistoryDialogProps) {
  const [loadHistory, { data, isFetching, isError }] =
    useLazyGetAssignedTagHistoryQuery();

  // Fetch only once the dialog is actually open, and re-fetch when it is pointed at a different
  // record. Reading `data` unconditionally would be wrong if this were `skip`-gated — RTK Query
  // keeps the previous response when a query is skipped — which is exactly why this is lazy.
  useEffect(() => {
    if (open) {
      loadHistory({ type: tagType, object_id: modelId });
    }
  }, [open, tagType, modelId, loadHistory]);

  const events: AssignedTagHistoryEvent[] = open ? (data ?? []) : [];

  const renderBody = () => {
    if (isFetching) {
      return <CircularProgress sx={{ width: "100%", py: 2 }} />;
    }

    if (isError) {
      return (
        <Typography
          variant="body2"
          color="error"
          data-testid="tag-history-error"
        >
          We could not load this tag history. Please try again.
        </Typography>
      );
    }

    if (!events.length) {
      return (
        <Typography variant="body2" color={grey[700]}>
          No tags have been added to this record.
        </Typography>
      );
    }

    return (
      <Stack spacing={1.5}>
        {/* Five columns, three of them nowrap, inside a dialog that narrows on small screens:
            without its own scroll container the table pushes the whole page sideways. */}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" data-testid="tag-history-table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>When</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Action</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Details</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow
                  key={event.id}
                  data-testid={`tag-history-row-${event.id}`}
                >
                  <TableCell sx={{ whiteSpace: "nowrap", color: grey[700] }}>
                    {formatEventDate(event.datetime)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography
                      variant="body2"
                      component="span"
                      fontWeight={500}
                      color={ACTION_COLORS[event.action] ?? "text.primary"}
                    >
                      {event.action}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TagHistoryChip
                      tag={event.tag}
                      removed={event.is_removed_tag}
                    />
                  </TableCell>
                  <TableCell sx={{ color: grey[700] }}>
                    {event.changes ?? "--"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {event.actor_label}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box>
          <Typography variant="caption" color={grey[700]}>
            Removed tags are shown struck through. History covers every tag ever
            applied to this record, including tags added before this feature was
            released.
          </Typography>
        </Box>
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Tag History"
      subtitle={subtitle}
      maxWidth="md"
      sx={HISTORY_DIALOG_SX}
    >
      <Box data-testid="tag-history-dialog">{renderBody()}</Box>
    </Dialog>
  );
}
